import React, { useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useTranslation } from '@lyearn/i18n';
import { Delete } from '@lyearn/icons';
import { LinkButton, useSnackbar } from '@lyearn/ui';
import { useCreateBookmarkMutation } from '@/features/bookmark/graphql/mutations/generated/createBookmark';
import { useDeleteBookmarkMutation } from '@/features/bookmark/graphql/mutations/generated/deleteBookmark';
import {
  BookmarkEntityTypeEnum,
  CreateBookmarkInput,
  DeleteBookmarkInput,
  ResourceTypeEnum,
} from '@/types/schema';

import { bookmarkAnimationStyles } from './styles';

export const NOT_ALLOWED_TYPES_FOR_BOOKMARK = [
  ResourceTypeEnum.Homework,
  ResourceTypeEnum.Task,
  // Sessions (Events)
];

const getBookmarkEntityType = (type: ResourceTypeEnum): BookmarkEntityTypeEnum => {
  if (Object.values(BookmarkEntityTypeEnum).includes(type as unknown as BookmarkEntityTypeEnum)) {
    return type as unknown as BookmarkEntityTypeEnum;
  }
  return BookmarkEntityTypeEnum.Article;
};

export interface UseContentBookmarkParams {
  bookmarkId?: string;
  contentId: string;
  contentType: ResourceTypeEnum;
  allowUndo?: boolean;
  isDisabled?: boolean;
  showLabel?: boolean;
  onBookmarkUpdate?: (id: string | undefined) => void;
}

const useContentBookmark = ({
  bookmarkId,
  contentId,
  contentType,
  allowUndo,
  onBookmarkUpdate,
}: UseContentBookmarkParams) => {
  const { t } = useTranslation();
  const iconAnimationRef = React.useRef('');
  const { showSnackbar, showErrorSnackbar, closeSnackbar } = useSnackbar();
  const [createBookmarkResponse, executeCreateBookmark] = useCreateBookmarkMutation();
  const [deleteBookmarkResponse, executeDeleteBookmark] = useDeleteBookmarkMutation();
  const [updatedBookmarkId, setUpdatedBookmarkId] = useState<string | undefined>(bookmarkId);

  const onCreateBookmark = React.useCallback(
    (input: CreateBookmarkInput) => {
      return executeCreateBookmark({ input }).then((response) => {
        if (response.error) {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        } else {
          const newId = response.data?.createBookmark?.bookmark?.id;
          setUpdatedBookmarkId(newId);
          onBookmarkUpdate?.(newId);
        }
      });
    },
    [executeCreateBookmark, onBookmarkUpdate, showErrorSnackbar, t],
  );

  const onDeleteBookmark = React.useCallback(
    (input: DeleteBookmarkInput) => {
      const onUndo = () => {
        closeSnackbar();
        onCreateBookmark({ entityId: contentId, entityType: getBookmarkEntityType(contentType) });
      };

      return executeDeleteBookmark({ input }).then((response) => {
        if (response.error) {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
          return;
        } else if (allowUndo) {
          showSnackbar({
            startIcon: <Delete className="h-20 w-20" fillColorToken="icon-placeholder" />,
            message: t('ContentCards.BookmarkRemoved'),
            action: (
              <LinkButton className="flex-shrink-0 py-2 px-4" color={'inverse'} onClick={onUndo}>
                {t('Common:Actions.Undo')}
              </LinkButton>
            ),
          });
        }
        setUpdatedBookmarkId(undefined);
        onBookmarkUpdate?.(undefined);
      });
    },
    [
      closeSnackbar,
      contentId,
      contentType,
      executeDeleteBookmark,
      onBookmarkUpdate,
      onCreateBookmark,
      showErrorSnackbar,
      showSnackbar,
      allowUndo,
      t,
    ],
  );

  const onToggleBookmark = React.useCallback(() => {
    if (updatedBookmarkId) {
      onDeleteBookmark({ bookmarkId: updatedBookmarkId });
    } else {
      onCreateBookmark({ entityId: contentId, entityType: getBookmarkEntityType(contentType) });
    }
  }, [contentId, contentType, onCreateBookmark, onDeleteBookmark, updatedBookmarkId]);

  useUpdateEffect(() => {
    iconAnimationRef.current = bookmarkAnimationStyles;
  }, [bookmarkId]);

  return {
    error: createBookmarkResponse.error || deleteBookmarkResponse.error,
    fetching: createBookmarkResponse.fetching || deleteBookmarkResponse.fetching,
    iconAnimationRef,
    onToggleBookmark,
    updatedBookmarkId,
  };
};

export default useContentBookmark;
